/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {DataTableColumn, DataTableRow} from '@ion/dls/src/components/table/current/DataTable';
import {DTCCodes, DtcEventType} from '@ion/dls/src/types/types';
import {downloadCsv, toCsv} from '../../utils/csv';

// the fault codes table's asset columns, shared by every row ...
export type FaultCodeAssetInfo = {
  groupName: string;
  assetName: string;
  assetType: string;
};

const FAULT_CODE_COLUMN_KEYS = ['groupName', 'assetName', 'assetType', 'dtcCode', 'description'];

// the exported CSV's file name suffix - a fixed slug, not translated ...
const FAULT_CODES_FILE_NAME = 'fault-codes';

// the fixed widths of every column but Description, which takes the rest ...
const FAULT_CODE_COLUMN_WIDTHS: Record<string, number> = {
  groupName : 114,
  assetName : 171,
  assetType : 121,
  dtcCode   : 114
};

/**
 * Builds the Fault Codes table's columns - Group Name, Asset Name, Asset
 * Type, DTC Code and Description (which takes the remaining width).
 *
 * ```typescript
 *
 *   import {getFaultCodeColumns} from '@/routes/tracking/faultCodeRows';
 *   ...
 *
 *   const columns = getFaultCodeColumns();
 *
 * ```
 *
 * @returns The table's columns.
 */
export const getFaultCodeColumns = (): DataTableColumn[] => FAULT_CODE_COLUMN_KEYS.map(function(key) {
  return {
    key,
    label : translate(`tracking:assetDetail.diagnostics.faultCodes.columns.${key}`),
    width : FAULT_CODE_COLUMN_WIDTHS[key]
  };
});

// a J1939 (DTC2) event's SPN / Source Address / FMI codes, each its own
// fault - the same breakdown legacy's FaultChipData shows as chips ...
const getDtc2Faults = (event: GenericObject) => Object.keys(event ?? {}).reduce(function(faults, key) {
  if (key === DTCCodes.SPN) {
    faults.push({dtcCode: event[key], description: event.spnDescription});
  } else if (key === DTCCodes.SourceAddress) {
    faults.push({dtcCode: event[key], description: translate('tracking:assetDetail.diagnostics.sourceAddress')});
  } else if (key === DTCCodes.FMI) {
    faults.push({dtcCode: event[key], description: event.fmiDescription});
  }
  return faults;
}, [] as GenericObject[]);

/**
 * Builds the Fault Codes table's rows - one per fault code, each with the
 * asset's group, name and type. J1939 (DTC2) events break down into their
 * SPN, Source Address and FMI codes, as legacy's fault chips do.
 *
 * ```typescript
 *
 *   import {getFaultCodeRows} from '@/routes/tracking/faultCodeRows';
 *   ...
 *
 *   const rows = getFaultCodeRows(faultData, dtcEventType, assetInfo);
 *
 * ```
 *
 * @param   events    The asset's fault events.
 * @param   eventType The events' DTC type.
 * @param   assetInfo The asset's group, name and type.
 * @returns           The table's rows.
 */
export const getFaultCodeRows = (
  events: GenericObject[],
  eventType: DtcEventType,
  assetInfo: FaultCodeAssetInfo
): DataTableRow[] => {
  const faults = eventType === DtcEventType.Dtc2Events ? (events ?? []).flatMap(getDtc2Faults) : (events ?? []);
  return faults.map(function(fault) {
    return {
      ...assetInfo,
      dtcCode     : String(fault?.dtcCode ?? ''),
      description : String(fault?.description ?? '')
    };
  });
};

/**
 * Downloads the Fault Codes table as a CSV file named
 * `<assetName>-fault-codes.csv` (or `fault-codes.csv` without an asset
 * name), through the shared `csv` util.
 *
 * ```typescript
 *
 *   import {downloadFaultCodesCsv} from '@/routes/tracking/faultCodeRows';
 *   ...
 *
 *   downloadFaultCodesCsv(columns, rows, assetName);
 *
 * ```
 *
 * @param   columns   The table's columns.
 * @param   rows      The table's rows.
 * @param   assetName The asset's name, for the file name.
 */
export const downloadFaultCodesCsv = (columns: DataTableColumn[], rows: DataTableRow[], assetName: string) => (
  downloadCsv(`${assetName ? `${assetName}-` : ''}${FAULT_CODES_FILE_NAME}.csv`, toCsv(columns, rows))
);
